<template>
  <div class="export">
    <div class="export__styles">
      <div
        v-for="style in styles"
        ref="styles"
        :key="style.id"
        class="export__style"
      >
        <StyleDetailsNew
          :style-id="style.id"
          :group-id="selectedGroupId"
          :show-slider-controls="false"
          pdf-export
          @click-navigation-left="() => {}"
          @click-navigation-right="() => {}"
          @close="() => {}"
        />
      </div>
    </div>

    <StitchDialog
      visible
      center
      :show-close="false"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      title="Export styles"
    >
      <div class="export__progress-body">
        <ElProgress
          class="progress"
          type="dashboard"
          :percentage="progressPercentage"
          :status="progressPercentage === 100 ? 'success' : null"
        />

        <h2 v-if="progressPercentage === 100">
          Your styles are ready...<br>
          Your PDF will be <br>
          automatically downloaded.
        </h2>
        <div v-else>
          <h2>
            Preparing the styles... this may take several minutes
          </h2>
          <h2 v-if="stylesConvertedCount > 0">
            Exported {{ stylesConvertedCount }} of {{ styles.length }}.
          </h2>
        </div>
      </div>
      <div
        v-if="progressPercentage === 100"
        slot="footer"
        class="export__progress-footer"
      >
        <router-link :to="`/projects/${projectId}/present`">
          <ElButton type="text">
            Go back to styles presentation
          </ElButton>
        </router-link>
      </div>
    </StitchDialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { Workspace } from '@/mixins/workspace'
import { Projects } from '@/mixins/projects'

import { imageResolutions } from '@/constants/image'
import { S3Trim } from '@/mixins/s3Trim.js'
import StyleDetailsNew from '@/components/project/StyleDetailsNew.vue'
import html2canvas from 'html2canvas'
import JsPDF from 'jspdf'

export default {
  name: 'ExportStyles',

  components: {
    StyleDetailsNew
  },

  mixins: [Workspace, Projects, S3Trim],

  data: () => ({
    styles: [],
    stylesPreloadedCount: 0,
    stylesConvertedCount: 0,
    pdf: null
  }),

  computed: {
    ...mapGetters(['getBoardsByProjectId']),

    /**
     * @returns {number}
     */
    progressPercentage () {
      if (this.styles.length > 0) {
        return Math.round(
          ((this.stylesPreloadedCount + this.stylesConvertedCount) /
            (this.styles.length * 2)) *
            100
        )
      } else {
        return 0
      }
    },

    /**
     * @returns {string}
     */
    imageResolutionLarge () {
      return imageResolutions.STYLE
    },

    /**
     * @returns {string}
     */
    imageResolutionExtraLarge () {
      return imageResolutions.STYLE_FULL
    }
  },

  async created () {
    await this.loadStyles()
  },

  methods: {
    ...mapActions(['fetchProject', 'fetchBoard']),

    /**
     */
    async loadStyles () {
      await this.fetchProject({
        projectId: this.projectId
      })

      for (const board of this.project.boards) {
        await this.fetchBoard({
          projectId: this.projectId,
          boardId: board.id
        })
      }

      const boards = this.getBoardsByProjectId(this.projectId)
      for (const board of boards) {
        this.styles = [...this.styles, ...board.styles]
      }

      for (const style of this.styles) {
        await this.preloadImages(style)

        this.stylesPreloadedCount++
      }

      this.createExportPDF()
    },

    /**
     * @param   {object}  style
     *
     * @returns {Promise}
     */
    preloadImages (style) {
      return new Promise((resolve, reject) => {
        const images = [
          ...style.options.map(option => {
            return {
              type: 'option',
              file: option.assets_file
            }
          }),
          ...style.details.map(detail => {
            return {
              type: 'detail',
              file: detail.assets_file
            }
          })
        ]

        if (images.length === 0) {
          resolve()
        }

        let imageLoaded = 0
        for (const imageObject of images) {
          const image = new Image()
          image.crossOrigin = 'use-credentials'
          image.onload = () => {
            imageLoaded++

            if (imageLoaded === images.length) {
              resolve()
            }
          }

          if (imageObject.type === 'option') {
            image.src = this.s3Trim(
              imageObject.file,
              this.imageResolutionExtraLarge
            )
          } else if (imageObject.type === 'detail') {
            image.src = this.s3Trim(imageObject.file, this.imageResolutionLarge)
          }
        }
      })
    },

    /**
     */
    async createExportPDF () {
      await this.convertImagesToBase64()

      this.pdfDoc = new JsPDF({
        format: 'a4',
        orientation: 'landscape'
      })

      for (let index = 0; index < this.styles.length; index++) {
        const styleSnapshotCanvas = await this.createStyleSnapshot(
          this.$refs.styles[index]
        )

        const imageWidth =
          (styleSnapshotCanvas.width / styleSnapshotCanvas.height) *
          this.pdfDoc.internal.pageSize.getHeight()
        const imageMarginLeft =
          (this.pdfDoc.internal.pageSize.getWidth() - imageWidth) / 2

        this.pdfDoc.addImage(
          styleSnapshotCanvas,
          'PNG',
          imageMarginLeft,
          0,
          imageWidth,
          this.pdfDoc.internal.pageSize.getHeight()
        )

        if (index !== this.$refs.styles.length - 1) {
          this.pdfDoc.addPage()
        }

        this.stylesConvertedCount++
      }

      this.downloadExportPDF()
    },

    /**
     * @returns {Promise}
     */
    convertImagesToBase64 () {
      return new Promise((resolve, reject) => {
        const images = document.querySelectorAll('img')

        let imageDone = 0
        const imageCount = images.length
        const canvas = document.createElement('canvas')

        // @TODO
        // TODO for some reason locally,
        // Babel caches the whole view wich result to an error like this:
        // Uncaught (in promise) DOMException: Failed to execute 'toDataURL'
        //  on 'HTMLCanvasElement': Tainted canvases may not be exported.
        // webpack-internal:///./node_modules/cache-loader/dist/cjs.js?
        // !./node_modules/babel-loader/lib/index.js!./node_modules/cache-loader/dist/cjs.js
        // ?!./node_modules/vue-loader/lib/index.js?!./src/views/project/ExportBoards.vue?vue&type=script&lang=js&

        const sizeMultiplier = 3

        for (const image of images) {
          const widthMultiplied = image.width * sizeMultiplier
          const heightMultiplied = image.height * sizeMultiplier

          canvas.width = widthMultiplied
          canvas.height = heightMultiplied
          const context = canvas.getContext('2d')
          context.drawImage(image, 0, 0, widthMultiplied, heightMultiplied)

          image.onload = () => {
            imageDone++

            if (imageDone === imageCount) {
              resolve()
            }
          }

          image.src = canvas.toDataURL()
        }
      })
    },

    /**
     * @param   {Element} boardReference
     *
     * @returns {Promise}
     */
    createStyleSnapshot (boardReference) {
      return html2canvas(boardReference, {
        scale: 4
      })
    },

    /**
     */
    downloadExportPDF () {
      const downloadDate = new Date()
      const formattedDate =
        downloadDate.getDate() +
        '-' +
        (downloadDate.getMonth() + 1) +
        '-' +
        downloadDate.getFullYear()

      this.pdfDoc.save(`${this.project.name} - ${formattedDate}.pdf`)
    }
  }
}
</script>

<style lang="scss" scoped>
$a4-margin-left: 130px;
$a4-landscape-width: 29.7cm;
$a4-landscape-height: 21cm;

.export__styles {
  width: $a4-landscape-width;
  height: $a4-landscape-height;
  margin-left: $a4-margin-left;
  vertical-align: middle;

  /deep/ .el-loading-mask {
    display: none;
  }
}

.export__style {
  width: $a4-landscape-width;
  height: $a4-landscape-height;
  margin-bottom: $spacer-unit;
  padding: $spacer-unit-x4;
  background-color: $white;
}

.export__progress-body,
.export__progress-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.export__progress-body {
  width: 100%;

  .progress {
    margin-bottom: $spacer-unit-x2;
  }
}
</style>

<style lang="scss">
@media print {
  .el-popup-parent--hidden {
    .el-dialog__wrapper,
    .v-modal {
      display: none;
    }
  }
}
</style>
